import { useEffect, useState } from 'react';

interface Props {
  'aria-label': string;
  value: number | null;
  onCommit: (value: number | null) => void;
  disabled?: boolean | undefined;
  min?: number | undefined;
  max?: number | undefined;
  step?: string | undefined;
  invalid?: boolean | undefined;
  /** Empty input commits null instead of snapping back — the open-ended top
   *  bucket, whose "no ceiling" is a real value. */
  clearable?: boolean | undefined;
  placeholder?: string | undefined;
}

function toDraft(value: number | null): string {
  return value === null ? '' : String(value);
}

/**
 * Numeric table cell that keeps a local draft while typing and commits on
 * blur or Enter. Out-of-range, empty or unparsable input snaps back to the
 * last committed value instead of firing a half-typed number upstream — every
 * cell here holds a required number.
 */
export function NumberCell({
  value,
  onCommit,
  disabled = false,
  min,
  max,
  step = '1',
  invalid = false,
  clearable = false,
  placeholder,
  ...rest
}: Props) {
  const [draft, setDraft] = useState(toDraft(value));

  useEffect(() => {
    setDraft(toDraft(value));
  }, [value]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed === '') {
      if (!clearable) {
        setDraft(toDraft(value));
        return;
      }
      if (value !== null) onCommit(null);
      return;
    }
    const next = Number(trimmed);
    const outOfRange = (min !== undefined && next < min) || (max !== undefined && next > max);
    if (!Number.isFinite(next) || outOfRange) {
      setDraft(toDraft(value));
      return;
    }
    if (next !== value) onCommit(next);
  };

  return (
    <input
      type="number"
      inputMode="decimal"
      aria-label={rest['aria-label']}
      aria-invalid={invalid}
      value={draft}
      disabled={disabled}
      step={step}
      {...(placeholder !== undefined ? { placeholder } : {})}
      {...(min !== undefined ? { min } : {})}
      {...(max !== undefined ? { max } : {})}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
      }}
      className={`w-24 rounded border bg-[var(--co-surface)] px-2 py-1 text-right font-mono text-[12px] text-[var(--co-text)] outline-none focus:border-[var(--co-brand)] disabled:opacity-50 ${
        invalid ? 'border-[var(--co-error)]' : 'border-[var(--co-border)]'
      }`}
    />
  );
}
